import { Box, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { CARD_TEXT, ProfileCard } from './ProfileCard';
import { displayYear, exitDate, longDate } from '../profileText';
import type { ProfileAccommodationItem, StudentProfile } from '../studentProfileApi';
import { ROSTER } from '../tokens';

type OpenPlan = (studentId: string, schoolYear?: string) => void;

const LINK_SX = { textTransform: 'none', fontFamily: ROSTER.font, fontSize: 16, fontWeight: 600, lineHeight: '24px', p: 0, minWidth: 0, flexShrink: 0 } as const;
const MUTED = { ...CARD_TEXT.lead, color: ROSTER.muted } as const;
const DIVIDER = `1px solid ${ROSTER.border}`;

function PlanLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button onClick={onClick} sx={LINK_SX}>
      {label}
    </Button>
  );
}

function ColumnHead({ title, meta }: { title: string; meta: string }) {
  return (
    <Box data-testid="column-head" sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
      <Typography component="b" sx={CARD_TEXT.strong}>{title}</Typography>
      <Typography component="span" sx={MUTED}>{meta}</Typography>
    </Box>
  );
}

function Decision({ text }: { text: string }) {
  return (
    <Typography data-testid="decision" sx={{ ...MUTED, pb: '8px', borderBottom: DIVIDER }}>{text}</Typography>
  );
}

function Row({ label, right }: { label: string; right?: string }) {
  return (
    <Box data-testid="accommodation-row" sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', p: '8px 0', borderBottom: DIVIDER }}>
      <Typography component="span" sx={CARD_TEXT.lead}>{label}</Typography>
      {right && <Typography component="span" sx={MUTED}>{right}</Typography>}
    </Box>
  );
}

function Column({ id, children }: { id: 'classroom' | 'tests'; children: ReactNode }) {
  return (
    <Box data-testid={`accommodations-${id}`} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {children}
    </Box>
  );
}

function accessLine(items: ProfileAccommodationItem[], eligible: boolean | null, test: string): string | null {
  if (items.length > 0) return null;
  return eligible === false ? `None on ${test} · no IEP or 504 plan` : `None on ${test}`;
}

function testingSeason(schoolYear: string): string {
  const end = schoolYear.split('-')[0];
  return end ? `Spring ${Number(end) + 1} testing` : '';
}

function springOf(schoolYear: string): string {
  const end = schoolYear.split('-')[0];
  return end ? `spring ${Number(end) + 1}` : 'this year';
}

function newestPlan(profile: StudentProfile) {
  return profile.plans.reduce<StudentProfile['plans'][number] | null>((best, plan) => (best && best.schoolYear >= plan.schoolYear ? best : plan), null);
}

export function AccommodationsCard({ profile, onOpenPlan }: { profile: StudentProfile; onOpenPlan: OpenPlan }) {
  const block = profile.accommodations;
  const year = block.schoolYear ?? profile.currentSchoolYear;
  const planId = block.planStudentId ?? profile.student.id;
  const currentPlan = year === profile.currentSchoolYear && profile.standing === 'active';
  const openShown = () => (currentPlan ? onOpenPlan(planId) : onOpenPlan(planId, year));
  const released = (schoolYear: string) => profile.role !== 'teacher' || schoolYear < profile.currentSchoolYear || (schoolYear === profile.currentSchoolYear && profile.plansGenerated);
  const releasedForTeacher = released(year);

  if (block.state === 'unknown') {
    return (
      <ProfileCard title="Accommodations" wide>
        <Typography sx={CARD_TEXT.small}>Accommodations could not be loaded. Refresh the page to try again.</Typography>
      </ProfileCard>
    );
  }
  if (block.state === 'draft') {
    return (
      <ProfileCard title="Accommodations" wide action={releasedForTeacher ? <PlanLink label="View in the plan" onClick={openShown} /> : undefined}>
        <Typography sx={MUTED}>{`None in place yet · the ${displayYear(year)} plan is a draft`}</Typography>
      </ProfileCard>
    );
  }
  if (block.state === 'none') {
    const newest = newestPlan(profile);
    const { exitYearEnd, exitMonth } = profile.elStatus;
    const line = exitYearEnd ? `None in place · exited ${exitDate(exitYearEnd, exitMonth ?? 0)}` : 'None in place';
    return (
      <ProfileCard title="Accommodations" wide action={newest && released(newest.schoolYear) ? <PlanLink label={`View ${displayYear(newest.schoolYear)} plan`} onClick={() => onOpenPlan(newest.studentId, newest.schoolYear)} /> : undefined}>
        <Typography sx={MUTED}>{line}</Typography>
      </ProfileCard>
    );
  }
  const decided = block.finalizedAt ? `Student Action Plan · ${longDate(block.finalizedAt)}` : 'Student Action Plan';
  const accessTest = block.alternateAccess ? 'Alternate ACCESS' : 'ACCESS';
  const access = accessLine(block.access, block.accessEligible, accessTest);
  return (
    <ProfileCard title="Accommodations" wide gap="24px" action={releasedForTeacher ? <PlanLink label="View in the plan" onClick={openShown} /> : undefined}>
      <Box data-testid="accommodations-columns" sx={{ display: 'grid', gap: '48px', gridTemplateColumns: 'minmax(0, 1fr)', '@container (min-width: 768px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } }}>
        <Column id="classroom">
          <ColumnHead title="Classroom" meta={displayYear(year)} />
          <Decision text={decided} />
          {block.classroom.map((item) => <Row key={item.label} label={item.label} />)}
          {block.classroom.length === 0 && <Typography sx={{ ...MUTED, pt: '8px' }}>None in place</Typography>}
        </Column>
        <Column id="tests">
          <ColumnHead title="Tests" meta={testingSeason(year)} />
          {block.testDecision ? (
            <>
              <Decision text={decided} />
              {block.contentTests.map((item) => <Row key={item.label} label={item.label} />)}
              {block.access.map((item) => <Row key={`access-${item.label}`} label={item.label} right={accessTest} />)}
              {access && <Typography data-testid="access-line" sx={{ ...MUTED, pt: '8px' }}>{access}</Typography>}
            </>
          ) : (
            <Typography sx={MUTED}>{`Not decided for ${springOf(year)} yet`}</Typography>
          )}
        </Column>
      </Box>
    </ProfileCard>
  );
}
